// ./src/hooks/useGloveboxDocuments.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  GloveboxDocument,
  GloveboxDocumentService,
  GloveboxDocumentFilter,
  GloveboxDocumentInput,
  GloveboxStats,
  Pagination,
  SortItem,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ============================================
// Types
// ============================================

export interface UseGloveboxDocumentsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<GloveboxDocumentFilter>;

  /** Initial sort configuration */
  initialSort?: SortItem[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Number of days to consider for "expiring soon" stats */
  expiringWithinDays?: number;
}

export interface UseGloveboxDocumentsReturn {
  // Data state
  document: GloveboxDocument | null;
  documents: GloveboxDocument[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Stats
  stats: GloveboxStats | null;
  loadingStats: boolean;

  // Search and filters
  filters: Partial<GloveboxDocumentFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedDocument: GloveboxDocument | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<GloveboxDocumentFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: GloveboxDocumentInput) => Promise<OpResult>;
  update: (id: string, params: GloveboxDocumentInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Stats operations
  getStats: (withinDays?: number) => Promise<OpResult>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedDocument: (document: GloveboxDocument | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getDocumentById: (id: string | undefined) => GloveboxDocument | undefined;

  // Filtered lists helpers
  getExpiredDocuments: () => GloveboxDocument[];
  getExpiringSoonDocuments: (withinDays?: number) => GloveboxDocument[];
  getDocumentsByCategory: (category: string) => GloveboxDocument[];
  getDocumentsByCarId: (carId: string) => GloveboxDocument[];
  getDocumentsByUserId: (userId: string) => GloveboxDocument[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [
  { name: "expiresAt", order: "ASC" },
  { name: "createdAt", order: "DESC" },
];

// ============================================
// Hook Implementation
// ============================================

export const useGloveboxDocuments = (
  options: UseGloveboxDocumentsOptions = {}
): UseGloveboxDocumentsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = { status: [STATUSES.ACTIVE] },
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    expiringWithinDays = 30,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortItem[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const expiringWithinDaysRef = useRef(expiringWithinDays);

  // Ref to track the last-queried filters for loadMore
  // so we never read stale `filters` state from a closure.
  const lastQueriedFiltersRef = useRef<Partial<GloveboxDocumentFilter>>(initialFilters);

  // Monotonically increasing call ID to detect stale list() responses.
  // When a new list() call starts, it increments this counter;
  // when the response arrives, it checks whether its ID still matches.
  const listCallIdRef = useRef(0);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  useEffect(() => {
    expiringWithinDaysRef.current = expiringWithinDays;
  }, [expiringWithinDays]);

  // Data state
  const [document, setDocument] = useState<GloveboxDocument | null>(null);
  const [documents, setDocuments] = useState<GloveboxDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Stats state
  const [stats, setStats] = useState<GloveboxStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<GloveboxDocumentFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<GloveboxDocument | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && documents.length === 0,
    [isLoaded, documents.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: GloveboxDocument) => {
    setDocuments((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === updatedItem.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setDocuments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setDocuments((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls so their responses are ignored
    listCallIdRef.current++;

    setDocument(null);
    setDocuments([]);
    setStats(null);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setLoadingStats(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedDocument(null);
  }, []);

  // ==========================================
  // Stats Helper
  // ==========================================

  // Refresh stats in background (fire-and-forget after mutations)
  const refreshStatsInBackground = useCallback(() => {
    GloveboxDocumentService.getStats(expiringWithinDaysRef.current).then(
      (statsResult) => {
        if (statsResult.code === OP_RESULT_CODES.OK && statsResult.data) {
          setStats((statsResult.data as GloveboxStats[])[0]);
        }
      }
    );
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // Get single document
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await GloveboxDocumentService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as GloveboxDocument[])[0];
          setDocument(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load document"));
        setDocument(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load document"));
        setDocument(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List documents.
   * - shouldReset=true (default): replaces documents array (page 1 load / filter change)
   * - shouldReset=false: appends to existing documents (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   * This prevents out-of-order responses from overwriting fresher data
   * (e.g. when the user changes filters rapidly).
   */
  const list = useCallback(
    async (
      filter: Partial<GloveboxDocumentFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Don't clear documents here — keep stale data visible while loading.
        // The array is replaced atomically when the response arrives.
        setCurrentPage(1);
      }

      setLastError(null);

      // Store the queried filters/sort so loadMore can use them via refs
      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await GloveboxDocumentService.list(
          filter as GloveboxDocumentFilter,
          paginationParams,
          currentSortBy
        );

        // Guard: if a newer list() call was initiated while we were waiting,
        // discard this response to avoid overwriting fresher data.
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as GloveboxDocument[])
            : [];

          if (isAppending) {
            setDocuments((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setDocuments(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load documents"));
        setIsLoaded(true);
        if (!isAppending) setDocuments([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load documents"));
        setIsLoaded(true);
        if (!isAppending) setDocuments([]);
        return result;
      } finally {
        // Only update loading flags if this call is still the latest.
        // A stale call should not flip loading to false while a newer
        // call is still in-flight.
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // Get many documents by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await GloveboxDocumentService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as GloveboxDocument[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load documents"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load documents"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // Get stats for dashboard
  const getStats = useCallback(async (withinDays?: number): Promise<OpResult> => {
    setLoadingStats(true);

    try {
      const result = await GloveboxDocumentService.getStats(
        withinDays || expiringWithinDaysRef.current
      );

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as GloveboxStats[])[0];
        setStats(data);
        return result;
      }

      setStats(null);
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setStats(null);
      return result;
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Create document
  const create = useCallback(
    async (params: GloveboxDocumentInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await GloveboxDocumentService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as GloveboxDocument[])[0];
          setDocument(newItem);
          setDocuments((prev) => [newItem, ...prev]);
          setLastError(null);
          refreshStatsInBackground();
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create document"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create document"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    [refreshStatsInBackground]
  );

  // Update document
  const update = useCallback(
    async (id: string, params: GloveboxDocumentInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Document ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await GloveboxDocumentService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as GloveboxDocument[])[0];
          setDocument(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          refreshStatsInBackground();
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update document"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update document"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList, refreshStatsInBackground]
  );

  // Remove document
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Document ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await GloveboxDocumentService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setDocument(null);
          removeFromList(id);
          setLastError(null);
          refreshStatsInBackground();
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove document"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove document"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList, refreshStatsInBackground]
  );

  // Remove many documents
  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one document ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await GloveboxDocumentService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setDocument(null);
          removeManyFromList(ids);
          setLastError(null);
          refreshStatsInBackground();
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove documents"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to remove documents")
        );
        return result;
      }
    },
    [removeManyFromList, refreshStatsInBackground]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Load more documents
  // Uses refs for filters and sort to avoid stale closures —
  // loadMore always reads whatever was last passed to list().
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    await list(
      lastQueriedFiltersRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      sortByRef.current,
      false
    );
  }, [list, loadingMore, hasMore, currentPage]);

  // Refresh documents (reset to page 1 + stats)
  // Uses the ref so it always picks up the last-queried filters,
  // not a potentially stale `filters` state snapshot.
  const refresh = useCallback(async (): Promise<OpResult> => {
    getStats();
    return list(
      lastQueriedFiltersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, getStats]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  // Lookup helper
  const getDocumentById = useCallback(
    (id: string | undefined): GloveboxDocument | undefined => {
      if (!id) return undefined;
      return documents.find((item) => item.id === id);
    },
    [documents]
  );

  // Get expired documents
  const getExpiredDocuments = useCallback((): GloveboxDocument[] => {
    return documents.filter((doc) => doc.isExpired === true);
  }, [documents]);

  // Get documents expiring soon
  const getExpiringSoonDocuments = useCallback(
    (withinDays?: number): GloveboxDocument[] => {
      const days = withinDays || expiringWithinDaysRef.current;
      return documents.filter((doc) => {
        if (doc.isExpired) return false;
        if (doc.daysUntilExpiration === null || doc.daysUntilExpiration === undefined)
          return false;
        return doc.daysUntilExpiration >= 0 && doc.daysUntilExpiration <= days;
      });
    },
    [documents]
  );

  // Get documents by category
  const getDocumentsByCategory = useCallback(
    (category: string): GloveboxDocument[] => {
      return documents.filter((doc) => doc.docType?.category === category);
    },
    [documents]
  );

  // Get documents by car ID
  const getDocumentsByCarId = useCallback(
    (carId: string): GloveboxDocument[] => {
      return documents.filter((doc) => doc.carId === carId);
    },
    [documents]
  );

  // Get documents by user ID
  const getDocumentsByUserId = useCallback(
    (targetUserId: string): GloveboxDocument[] => {
      return documents.filter((doc) => doc.userId === targetUserId);
    },
    [documents]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
        getStats();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
        getStats();
      }
    }
  }, [userId, autoLoad, list, getStats, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      document,
      documents,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,

      // Stats
      stats,
      loadingStats,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      selectedDocument,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Stats operations
      getStats,

      // Additional convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedDocument,

      // Helper operations
      reset,

      // Lookup helpers
      getDocumentById,

      // Filtered lists helpers
      getExpiredDocuments,
      getExpiringSoonDocuments,
      getDocumentsByCategory,
      getDocumentsByCarId,
      getDocumentsByUserId,

      // Error handling
      lastError,
      clearError,
    }),
    [
      document,
      documents,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,
      stats,
      loadingStats,
      filters,
      sortBy,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      selectedDocument,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      getStats,
      loadMore,
      refresh,
      getDocumentById,
      getExpiredDocuments,
      getExpiringSoonDocuments,
      getDocumentsByCategory,
      getDocumentsByCarId,
      getDocumentsByUserId,
      lastError,
      clearError,
      reset,
    ]
  );
};